import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getAuth, type Auth } from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';

const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

function reportFatalConfigError(error: unknown) {
    if (typeof document === 'undefined') return;
    const root = document.getElementById('root');
    if (!root) return;
    const message = error instanceof Error ? error.message : String(error);
    root.textContent = '';
    const panel = document.createElement('div');
    panel.style.cssText = 'padding:2rem;font-family:system-ui,sans-serif;color:#fecdd3;background:#0b1020;min-height:100vh';
    const heading = document.createElement('h1');
    heading.textContent = 'Firebase configuration missing';
    const detail = document.createElement('p');
    detail.textContent = message;
    const hint = document.createElement('p');
    hint.textContent = 'Copy .env.example to .env.local in the repo root and restart the dev server. '
        + 'Its defaults run the app in mock mode, which needs no Firebase project.';
    panel.append(heading, detail, hint);
    root.append(panel);
}

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;
let db: Firestore;

if (import.meta.env.VITE_USE_MOCK_AUTH === 'true') {
    // Mock mode (services/mock/*) never issues a real Firebase call, so a
    // missing/placeholder VITE_FIREBASE_* config must not crash import here.
    // Most modules reach Firebase through a service layer that Vite aliases
    // to a mock in this mode, but a couple (e.g. SocialLogin.tsx) still
    // import `auth`/`db` directly, so real initialization must be skipped
    // rather than relying on every caller being aliased away.
    app = {} as unknown as FirebaseApp;
    auth = {} as unknown as Auth;
    db = {} as unknown as Firestore;
} else {
    try {
        app = initializeApp(firebaseConfig);
        auth = getAuth(app);
        db = getFirestore(app);
    } catch (error) {
        console.error('Failed to initialize Firebase:', error);
        // This runs at import time, before React mounts, so an unexplained
        // throw here renders a blank white page (issue #115). Say what's
        // actually wrong in the document itself before rethrowing.
        reportFatalConfigError(error);
        throw error;
    }
}

export { app, auth, db };
export default app;
